<template>
  <div class="introduction-container">
    <div class="breadcrumbs mb-40">
      <breadcrumb path="/documentation/introduction" name="Homepage" :routeName="route.name" />
    </div>
    <div class="title">
      {{ $t('introduction_page.intro_title') }}
    </div>
    <div class="submenu-container">
      <div class="mb-10">
        {{ $t('introduction_page.intro_desc1') }}<br>
        {{ $t('introduction_page.intro_desc2') }}<br>
      </div>
      <div class="mt-20 mb-20">
        <span class="font-w-bold">API URL</span><br>
        {{ apiUrl }}<br>
      </div>
      <div class="mb-10">
        {{ $t('introduction_page.intro_desc3') }}<br>
      </div>
      <div class="mb-10">
        1. {{ $t('introduction_page.global_list') }}<br>
        2. {{ $t('introduction_page.customer_list') }}<br>
        3. {{ $t('introduction_page.product_list') }}<br>
        4. {{ $t('introduction_page.order_management') }}<br>
        5. {{ $t('introduction_page.purchase') }}<br>
      </div>
      <div class="mt-20 mb-20">
        {{ $t('introduction_page.intro_desc4_a') }}
        <span class="font-w-bold">{{ $t('introduction_page.console_app') }}</span>
        ({{ appPath }})
        {{ $t('introduction_page.intro_desc4_b') }}
      </div>
      <div class="mt-20 mb-20">
        {{ $t('introduction_page.intro_desc5') }}<br>
      </div>
      <div class="mb-10">
        {{ $t('introduction_page.intro_desc6') }}<br>
      </div>
      <div class="mb-10">
        <span>
          1. {{ $t('introduction_page.intro_desc8_list1_a') }}
          {{ tokenPath }}
          {{ $t('introduction_page.intro_desc8_list1_b') }}
        </span>
        <div class="code-editor mt-20 mb-20">
          <div class="sticky-header">
            <el-row justify="space-between">
              <el-col class="text-left" :span="4">
                <span class="font-w-bold">curl</span>
              </el-col>
              <el-col class="text-right" :span="8">
                <el-popover
                  ref="popover"
                  placement="left"
                  title=""
                  :width="20"
                  trigger="click"
                  content="Copied"
                >
                  <template #reference>
                    <el-button
                      type="primary"
                      size="default"
                      @click="copied(curlData)"
                    >
                      Copy to clipboard
                    </el-button>
                  </template>
                </el-popover>
              </el-col>
            </el-row>
          </div>
          <el-scrollbar>
            <pre>{{ curlData }}</pre>
          </el-scrollbar>
        </div>
        <span>2. {{ $t('introduction_page.intro_desc8_list2') }}</span>
        <div class="code-editor mt-20 mb-20">
          <div class="sticky-header">
            <el-row justify="space-between">
              <el-col class="text-left" :span="4">
                <span class="font-w-bold">json</span>
              </el-col>
              <el-col class="text-right" :span="8">
                <el-popover
                  ref="popover"
                  placement="left"
                  title=""
                  :width="20"
                  trigger="click"
                  content="Copied"
                >
                  <template #reference>
                    <el-button
                      type="primary"
                      size="default"
                      @click="copied(tokenData)"
                    >
                      Copy to clipboard
                    </el-button>
                  </template>
                </el-popover>
              </el-col>
            </el-row>
          </div>
          <el-scrollbar>
            <pre>{{ tokenData }}</pre>
          </el-scrollbar>
        </div>
      </div>
    </div>
    <div class="title mt-40">
      {{ $t('introduction_page.language') }}
    </div>
    <div class="submenu-container">
      <div class="mb-20">
        {{ $t('introduction_page.intro_desc9') }}
      </div>
      <div class="mb-10">
        {{ $t('introduction_page.example') }} => [id]<br>
        {{ apiUrl }}admin/v1/<span class="font-w-bold">id</span>/product
      </div>
      <div class="mb-10">
        {{ $t('introduction_page.example') }} => [en]<br>
        {{ apiUrl }}admin/v1/<span class="font-w-bold">en</span>/product
      </div>
    </div>
    <div class="title mt-40">
      {{ $t('introduction_page.quick_start_title') }}
    </div>
    <div class="submenu-container mb-70">
      {{ $t('introduction_page.quick_start_desc1') }}
      <router-link to="/documentation/developer-guide">
        {{ $t('introduction_page.api_reference') }}
      </router-link>
      {{ $t('introduction_page.quick_start_desc2') }}
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useRoute } from 'vue-router'
import Breadcrumb from '@/components/layout/Breadcrumb.vue'
const route = useRoute()
const apiUrl = ref(process.env.VUE_APP_BASE_API_URL)
const appPath = ref(process.env.VUE_APP_BASE_WEB_URL + 'console/app-list')
const tokenPath = ref(process.env.VUE_APP_BASE_API_URL + 'token')
const curlData = ref('curl --location --request POST ' + tokenPath.value + ' \\--header \'Accept: application/json\' \\--form \'app_id="8R7yXPhDHJ0YG2S9FXkd\"\' \\--form \'secret_key=\"Ag9naViTNoSEAw24BN7WLriZipxPzRzc\"\' \\--form \'grant_type=\"secret_key"\'')
const tokenData = ref({
  token_type: 'Bearer',
  expires_in: 86400,
  access_token: 'eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJpc3MiOiJodHRwOlwvXC8xMjcuMC4wLjE6ODAwM1wvYXBpXC9vcGVuLWFwaVwvdjFcL2lkXC90b2tlbiIsImlhdCI6MTY2MTQxMTMxMywiZXhwIjoxNjYxNDk3NzEzLCJuYmYiOjE2NjE0MTEzMTMsImp0aSI6IjY0MzNjOTU2M2Q5ZWU4ZDcxNTVmNGZhNTBiZThjYTgwMjJjODBjMGZkYTBkNjU4ZjNkOTQzOWRkZjkwZDA4NGMzNjYzZWEwN2I2M2U2YTNkMTY2MTQxMTMxMyIsInN1YiI6NDAsInBydiI6IjQyNWM2NjJkYzFlODA5ZDNlMThjYTQ1NzYxZDBmNjFkODBiZjZjZTMifQ.488ZF-i7VdV6QjkYDkIMMNk-wzQ_aBFj-54je29KD4A',
  refresh_token: '553ed7d4ea5e66f440c28e6260cb502ea80259ca083f62a1db0ba272024081f3a0675dcd224d8c691661411313',
  expires_refresh_token: 2592000
})
const copied = (data) => {
  const response = jsonPretty(data)
  navigator.clipboard.writeText(response)
}
const jsonPretty = (json) => {
  return JSON.stringify(json, null, 4)
}
</script>

<style lang="scss" scoped>
.submenu-container {
  max-width: 75%;
}
.code-editor {
  height: auto;
  width: 900px;
  overflow: hidden;
  & .sticky-header {
    top: 0px;
    padding: 4px 8px 4px 8px;
    position: sticky;
    background: #F2F2F2;
  }
  & .el-scrollbar {
    max-height: 240px;
    overflow-x: hidden;
    background-color: #f7f6f6;
    border-radius: 2px;
  }
  & .el-scrollbar__view pre {
    padding: 20px;
    font-size: 14px;
    font-weight: 600;
    line-height: 24px;
  }
}
</style>
